import { Flex } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { InputControlWithQuestionId } from "../InputControlWithQuestionId";

export const FormikSchema = z.object({
  link: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("enabled"),
      url: z.string().url(),
      text: z.string().min(1, "This field is required"),
    }),
    z.object({
      type: z.literal("disabled"),
      url: z.string(),
      text: z.string(),
    }),
  ]),
});

export type FormikType = z.infer<typeof FormikSchema>;

export function toFormValue(question: AF.V3.Question): FormikType {
  return {
    link: question.link_url
      ? {
          type: "enabled",
          url: question.link_url,
          text: question.link_text ?? question.link_url,
        }
      : { type: "disabled", url: "", text: "" },
  };
}

type Props = { questionId: uuid };
export const QuestionLink: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();
  const questionLink = formik.values.link;

  if (questionLink.type === "disabled") {
    return null;
  }

  return (
    <Flex direction="column" gap="3">
      <InputControlWithQuestionId
        name="link.text"
        label="Question link text"
        questionId={questionId}
      />
      <InputControlWithQuestionId
        name="link.url"
        label="Question link URL"
        questionId={questionId}
      />
    </Flex>
  );
};

export const QuestionLinkSwitch: React.FC<Props> = ({ questionId }) => {
  const id = `questionLinkSwitch-${questionId}`;
  return (
    <FormikSwitchableContent<"enabled" | "disabled">
      id={id}
      label="Show link for question"
      name="link.type"
      checked={["enabled", ""]}
      unchecked={["disabled", ""]}
      showContent={(value) => value === "enabled"}
      description=""
    >
      <QuestionLink questionId={questionId} />
    </FormikSwitchableContent>
  );
};
